import React, { useState } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFile,
  faFilePdf,
  faFileExcel,
  faFileWord,
  faFileImage,
  faDownload,
  faFolderClosed,
  faFolderOpen,
  faTimes,
  faSearch,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";

const DigitalFilesModal = ({ isOpen, onClose, files }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [hoveredFile, setHoveredFile] = useState(null);

  if (!isOpen || !files) return null;

  // Group files by Prefijo
  const filesByPrefijo = files.reduce((acc, file) => {
    const { Prefijo } = file;
    acc[Prefijo] = acc[Prefijo] || [];
    acc[Prefijo].push(file);
    return acc;
  }, {});

  // Filter files based on search term
  const filteredGroups = Object.entries(filesByPrefijo).filter(
    ([prefijo, groupFiles]) => {
      const lowerSearchTerm = searchTerm.toLowerCase();

      // If search is empty, show all groups
      if (!searchTerm) return true;

      // Check if prefijo matches search
      if (prefijo.toLowerCase().includes(lowerSearchTerm)) return true;

      // Check if any file in the group matches search
      return groupFiles.some(
        (file) =>
          (file.Note && file.Note.toLowerCase().includes(lowerSearchTerm)) ||
          (file.Ext && file.Ext.toLowerCase().includes(lowerSearchTerm))
      );
    }
  );

  // Toggle group expansion
  const toggleGroup = (prefijo) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [prefijo]: !prev[prefijo],
    }));
  };

  // Handle file download
  const handleFileClick = (file) => {
    const fileName = file.Note || "default_file";
    const fileExtension = file.Ext || fileName.split(".").pop().toLowerCase();
    const href = ["jpg", "jpeg", "png"].includes(fileExtension)
      ? `data:image/${fileExtension};base64,${file.DocBytes}`
      : `data:application/octet-stream;base64,${file.DocBytes}`;

    const link = document.createElement("a");
    link.href = href;
    link.download = `${fileName}.${fileExtension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download files as a zip
  const downloadFilesAsZip = (files, zipName) => {
    const zip = new JSZip();
    files.forEach((file) => {
      const fileName = `${file.Note || "file"}.${file.Ext || "bin"}`;
      zip.file(fileName, file.DocBytes, { base64: true });
    });
    zip
      .generateAsync({ type: "blob" })
      .then((content) => saveAs(content, `${zipName}.zip`));
  };

  // Handle downloading all files
  const handleDownloadAll = () => {
    const allFiles = Object.values(filesByPrefijo).flat();
    downloadFilesAsZip(allFiles, "Digital_Vault_Files");
  };

  // Handle downloading a group of files
  const handleDownloadGroup = (groupFiles, groupName) => {
    downloadFilesAsZip(groupFiles, `${groupName}_Files`);
  };

  // Toggle file selection
  const toggleFileSelection = (file) => {
    setSelectedFiles((prev) => {
      const fileId = file.Note + file.Ext; // Use a combo of name and extension as a unique identifier
      const isSelected = prev.some((f) => f.Note + f.Ext === fileId);

      if (isSelected) {
        return prev.filter((f) => f.Note + f.Ext !== fileId);
      } else {
        return [...prev, file];
      }
    });
  };

  // Download selected files
  const handleDownloadSelected = () => {
    if (selectedFiles.length === 0) return;
    downloadFilesAsZip(selectedFiles, "Selected_Files");
  };

  // Get appropriate file icon based on extension
  const getFileIcon = (extension) => {
    const ext = extension ? extension.toLowerCase() : "";

    if (["jpg", "jpeg", "png", "gif", "bmp", "svg"].includes(ext)) {
      return faFileImage;
    } else if (["pdf"].includes(ext)) {
      return faFilePdf;
    } else if (["xls", "xlsx", "csv"].includes(ext)) {
      return faFileExcel;
    } else if (["doc", "docx", "rtf"].includes(ext)) {
      return faFileWord;
    } else {
      return faFile;
    }
  };

  // Is a file selected?
  const isFileSelected = (file) => {
    const fileId = file.Note + file.Ext;
    return selectedFiles.some((f) => f.Note + f.Ext === fileId);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-75 flex items-center justify-center">
      <div className="min-h-screen w-full flex items-center justify-center p-5">
        <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white px-6 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <h2 className="text-2xl font-bold tracking-tight">
                  Digital Vault
                </h2>
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                  {files.length} files
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleDownloadAll}
                  className="flex items-center space-x-2 bg-blue-800 hover:bg-blue-950 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  <span>Download All</span>
                </button>
                {selectedFiles.length > 0 && (
                  <button
                    onClick={handleDownloadSelected}
                    className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                  >
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Download Selected ({selectedFiles.length})</span>
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-blue-800 rounded-full transition-colors duration-200"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xl" />
                </button>
              </div>
            </div>

            {/* Search bar */}
            <div className="mt-4 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-blue-300" />
              </div>
              <input
                type="text"
                placeholder="Search files by name or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-blue-800 bg-opacity-40 rounded-lg text-white placeholder-blue-300 border border-blue-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>

          {/* Content area */}
          <div className="max-h-[70vh] overflow-y-auto p-6 bg-gray-50">
            {filteredGroups.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-gray-500 text-lg">
                  No files match your search criteria
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredGroups.map(([prefijo, groupFiles]) => (
                  <div
                    key={prefijo}
                    className="bg-white rounded-lg shadow-md overflow-hidden"
                  >
                    <div
                      className="flex justify-between items-center px-5 py-4 bg-gradient-to-r from-blue-50 to-blue-100 cursor-pointer"
                      onClick={() => toggleGroup(prefijo)}
                    >
                      <div className="flex items-center space-x-3">
                        <FontAwesomeIcon
                          icon={
                            expandedGroups[prefijo]
                              ? faFolderOpen
                              : faFolderClosed
                          }
                          className="text-blue-700 text-lg"
                        />
                        <h3 className="text-lg font-medium text-blue-900">
                          {prefijo}
                        </h3>
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                          {groupFiles.length} files
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadGroup(groupFiles, prefijo);
                          }}
                          className="flex items-center space-x-1 text-blue-700 hover:text-blue-900 text-sm"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                          <span>Download Group</span>
                        </button>
                      </div>
                    </div>

                    {expandedGroups[prefijo] && (
                      <div className="p-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {groupFiles.map((file, index) => (
                          <div
                            key={index}
                            className={`relative group rounded-md overflow-hidden transition-all duration-200 hover:shadow-md ${
                              isFileSelected(file)
                                ? "ring-2 ring-blue-500 bg-blue-50"
                                : "border border-gray-200"
                            }`}
                            onMouseEnter={() => setHoveredFile(file)}
                            onMouseLeave={() => setHoveredFile(null)}
                          >
                            <div className="absolute top-2 right-2 z-10">
                              <div
                                className={`h-5 w-5 rounded-full flex items-center justify-center cursor-pointer ${
                                  isFileSelected(file)
                                    ? "bg-blue-500 text-white"
                                    : "bg-white border border-gray-300"
                                }`}
                                onClick={() => toggleFileSelection(file)}
                              >
                                {isFileSelected(file) && (
                                  <FontAwesomeIcon
                                    icon={faCheck}
                                    className="text-xs"
                                  />
                                )}
                              </div>
                            </div>

                            <div className="p-4 text-center flex flex-col items-center">
                              {["jpg", "jpeg", "png"].includes(
                                file.Ext || ""
                              ) ? (
                                <div className="h-28 w-full mb-3 flex items-center justify-center overflow-hidden rounded">
                                  <img
                                    src={`data:image/${file.Ext};base64,${file.DocBytes}`}
                                    alt={file.Note || "Image"}
                                    className="object-cover w-full h-full"
                                  />
                                </div>
                              ) : (
                                <div className="mb-3 h-28 flex items-center justify-center">
                                  <FontAwesomeIcon
                                    icon={getFileIcon(file.Ext)}
                                    className="text-blue-600 text-4xl"
                                  />
                                </div>
                              )}
                              <div className="flex-1 w-full">
                                <p
                                  className="text-sm font-medium text-gray-800 line-clamp-1"
                                  title={file.Note || "Unnamed file"}
                                >
                                  {file.Note || "Unnamed file"}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {file.Ext ? `.${file.Ext}` : "Unknown type"}
                                </p>
                              </div>
                            </div>

                            {/* Hover overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                              <button
                                onClick={() => handleFileClick(file)}
                                className="bg-white text-blue-700 hover:bg-blue-100 p-2 rounded-full transition-colors duration-200"
                              >
                                <FontAwesomeIcon icon={faDownload} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 bg-gray-100 border-t border-gray-200 flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {selectedFiles.length > 0
                ? `${selectedFiles.length} files selected`
                : "Select files to download"}
            </p>
            <div className="text-sm text-gray-600">
              Total files: {files.length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigitalFilesModal;
